import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Lesson } from 'core/models/course-content.model';
import { CourseEditorService } from 'core/services/course-editor.service';
import { ToastService } from 'core/services/toast.service';
import { ConfirmDialogService } from 'core/services/confirm-dialog.service';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-video-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LessonHeader],
  templateUrl: './video-editor.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class VideoEditor implements OnInit {
  @Input({ required: true }) lesson!: Lesson;
  @Output() deleteLesson = new EventEmitter<void>();

  private readonly sanitizer = inject(DomSanitizer);
  private readonly fb = inject(FormBuilder);
  private readonly courseService = inject(CourseEditorService);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);

  protected videoForm!: FormGroup;
  protected currentModuleId: string | null = null;

  ngOnInit(): void {
    const currentModule = this.courseService.getCurrentModule();
    this.currentModuleId = currentModule?.id || null;

    this.videoForm = this.fb.group({
      title: [
        this.lesson.title || '',
        [Validators.required, Validators.minLength(3)],
      ],
      description: [
        this.lesson.description || '',
        [Validators.required, Validators.minLength(10)],
      ],
      videoUrl: [this.lesson.videoUrl || '', [Validators.required]],
      duration: [
        this.lesson.duration || 0,
        [Validators.required, Validators.min(1)],
      ],
    });

    this.videoForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onFormChange();
      });
  }

  protected get safeVideoUrl(): SafeResourceUrl | null {
    const url = this.videoForm.get('videoUrl')?.value;
    if (!url) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  protected onSave(): void {
    if (this.videoForm.invalid) {
      this.videoForm.markAllAsTouched();
      this.toastService.error(
        'Por favor, preencha todos os campos obrigatórios corretamente.',
      );
      return;
    }

    // Buscar o módulo atual novamente caso não tenha sido encontrado no ngOnInit
    if (!this.currentModuleId) {
      const currentModule = this.courseService.getCurrentModule();
      this.currentModuleId = currentModule?.id || null;
    }

    if (!this.currentModuleId) {
      this.toastService.error(
        'Não foi possível identificar o módulo da lição. Tente novamente.',
      );
      return;
    }

    const formValue = this.videoForm.value;
    const updatedLesson = this.courseService.updateLesson(
      this.currentModuleId,
      this.lesson.id,
      {
        title: formValue.title,
        description: formValue.description,
        videoUrl: formValue.videoUrl,
        duration: formValue.duration,
      },
    );

    if (updatedLesson) {
      this.toastService.success('Lição de vídeo salva com sucesso!');
    } else {
      this.toastService.error('Erro ao salvar lição. Tente novamente.');
    }
  }

  private onFormChange(): void {
    if (this.videoForm.valid && this.currentModuleId) {
      const formValue = this.videoForm.value;
      this.courseService.updateLesson(this.currentModuleId, this.lesson.id, {
        title: formValue.title,
        description: formValue.description,
        videoUrl: formValue.videoUrl,
        duration: formValue.duration,
      });
    }
  }

  protected getFieldError(fieldName: string): string {
    const field = this.videoForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return '';
    }

    if (field.errors['required']) {
      return 'Este campo é obrigatório';
    }
    if (field.errors['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `Deve ter pelo menos ${requiredLength} caracteres`;
    }
    if (field.errors['min']) {
      return 'Duração deve ser maior que 0';
    }

    return '';
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.videoForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  protected async onDelete(): Promise<void> {
    const confirmed = await this.confirmDialogService.confirm({
      title: 'Excluir Lição',
      message: `Tem certeza que deseja excluir a lição "${this.lesson.title}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      this.deleteLesson.emit();
    }
  }
}
